import type { Vec3 } from '../types';

export type LineAxis = 'x' | 'y' | 'z';

export const LINE_AXIS_COLORS: Record<LineAxis, string> = {
  x: '#e8726d', y: '#8299f2', z: '#72c98a',
};

export function endpointAtLength3D(start: Vec3, toward: Vec3, length: number): Vec3 {
  const delta: Vec3 = [toward[0] - start[0], toward[1] - start[1], toward[2] - start[2]];
  const distance = Math.hypot(...delta);
  return distance > 0 ? [start[0] + delta[0] / distance * length, start[1] + delta[1] / distance * length, start[2] + delta[2] / distance * length] : [...start];
}

/** Find the closest projected world axis, then snap its signed distance in millimetres. */
export function axisSnap3D(
  start: Vec3,
  pointer: [number, number],
  project: (point: Vec3) => [number, number],
  step: number,
  tolerancePx = 10,
): { end: Vec3; axis: LineAxis } | null {
  const origin = project(start);
  let best: { end: Vec3; axis: LineAxis; distance: number } | null = null;
  for (const [axis, index] of [['x', 0], ['y', 1], ['z', 2]] as const) {
    const unit: Vec3 = [...start]; unit[index] += 1000;
    const target = project(unit);
    const dx = target[0] - origin[0], dy = target[1] - origin[1];
    const magnitude = dx * dx + dy * dy;
    if (magnitude < 16) continue;
    const px = pointer[0] - origin[0], py = pointer[1] - origin[1];
    const amount = (px * dx + py * dy) / magnitude;
    const distance = Math.hypot(px - amount * dx, py - amount * dy);
    if (distance > tolerancePx || Math.hypot(px, py) < 5 || (best && distance >= best.distance)) continue;
    const end: Vec3 = [...start];
    end[index] = start[index] + Math.round(amount * 1000 / step) * step;
    best = { end, axis, distance };
  }
  return best ? { end: best.end, axis: best.axis } : null;
}
